/**
 * outreachNotice — the pure half of the "you've been given an outreach list"
 * card (docs/crm-outreach-lists-plan.md §B7). React-free and api-free, so the
 * wording and the once-per-list rule are testable as plain functions.
 *
 * The rule: one card and one OS notification per list, per person, ever.
 *  - `notified` — lists we have already raised an OS notification for. That
 *    fires once, on first sight, so a reload never buzzes the desktop again.
 *  - `dismissed` — lists whose card was waved off or acted on ("Work
 *    through"). The card itself does NOT expire, and survives a reload until
 *    then: it may be the only word a recruiter gets that a list exists.
 * Both live in localStorage under a per-user key (a shared front-desk
 * browser must not mark one recruiter's list as seen for another).
 */
import type { WorklistOutreachCounts } from '../../outreach/types';

export type AssignedList = WorklistOutreachCounts['lists'][number];

export interface NoticeStore {
  notified: string[];
  dismissed: string[];
}

/** Ids kept per set. Far beyond the lists anyone is on at once. */
export const NOTICE_STORE_CAP = 200;

export const noticeStoreKey = (userId: string): string => `crm.outreachAssigned.v1:${userId}`;

const EMPTY: NoticeStore = { notified: [], dismissed: [] };

/** Tolerant: junk, an old shape, or nothing at all is an empty store. */
export function parseNoticeStore(raw: string | null): NoticeStore {
  if (!raw) return { ...EMPTY };
  try {
    const parsed = JSON.parse(raw);
    const strings = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
    return { notified: strings(parsed?.notified), dismissed: strings(parsed?.dismissed) };
  } catch {
    return { ...EMPTY };
  }
}

/** Newest wins past the cap: slice from the END of insertion order. */
export function serializeNoticeStore(s: NoticeStore): string {
  return JSON.stringify({
    notified: s.notified.slice(-NOTICE_STORE_CAP),
    dismissed: s.dismissed.slice(-NOTICE_STORE_CAP),
  });
}

/** Add ids to one set, keeping insertion order and no duplicates. */
export function withIds(s: NoticeStore, set: keyof NoticeStore, ids: string[]): NoticeStore {
  const have = new Set(s[set]);
  const added = ids.filter((id) => !have.has(id));
  return added.length ? { ...s, [set]: [...s[set], ...added] } : s;
}

/**
 * Which lists get a card, and which of those still owe their one OS
 * notification. A list with nothing of mine left open is not news.
 *
 * Nor is a list still being set up. The counts include lists that are
 * launching, and a launch adds people a hundred at a time, so a poll that
 * lands midway sees only the part added so far. The OS notification fires
 * once and is never corrected — "Luke gave you 100 people" when the real
 * share is 1,000 — and "Work through" would start a half-built list. So
 * nothing is raised until the launch has finished (`launched_at` is set
 * only then, together with the list going active).
 */
export function pickAssignments(
  lists: AssignedList[] | null | undefined,
  store: NoticeStore,
): { cards: AssignedList[]; toNotify: AssignedList[] } {
  const dismissed = new Set(store.dismissed);
  const notified = new Set(store.notified);
  const cards = (lists || []).filter((l) => l && l.id && l.launched_at && l.assigned_open > 0 && !dismissed.has(l.id));
  return { cards, toNotify: cards.filter((l) => !notified.has(l.id)) };
}

const firstName = (full: string | null | undefined): string => (full || '').trim().split(/\s+/)[0] || '';

/** "1 person" / "106 people". */
export const peopleCount = (n: number): string => (n === 1 ? '1 person' : `${n.toLocaleString()} people`);

/** "the Diabetic Neuropathy Study" — without doubling a title that already says "The". */
function studyPhrase(title: string | null | undefined): string | null {
  const t = (title || '').trim();
  if (!t) return null;
  return /^the\s/i.test(t) ? t : `the ${t}`;
}

/**
 * The card's sentence: "Luke gave you 106 people for the Diabetic Neuropathy
 * Study". When the creator is unknown — or is the viewer, who built a list and
 * put themselves on it — it drops the "gave you" framing rather than reading
 * "Luke gave you" to Luke.
 */
export function assignmentLine(list: AssignedList, viewerName?: string | null): string {
  const people = peopleCount(list.assigned_open);
  const study = studyPhrase(list.study_title);
  const creator = (list.created_by_name || '').trim();
  const self = !!creator && !!viewerName && creator.toLowerCase() === viewerName.trim().toLowerCase();
  if (!creator || self) {
    return study ? `You have ${people} to work through for ${study}` : `You have ${people} to work through`;
  }
  const who = firstName(creator);
  return study ? `${who} gave you ${people} for ${study}` : `${who} gave you ${people} to work through`;
}
